import moment from 'moment'

const millisecondsIn = {
    second: 1000,
    minute: 60000,
    hour: 3600000,
    day: 86400000,
    week: 60480000,
    month: 2628000000,
    year: 31536000000
}

const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
]

const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

// Helper functions
const nth = function (d) {
    if (d > 3 && d < 21) return 'th'
    switch (d % 10) {
        case 1:
            return 'st'
        case 2:
            return 'nd'
        case 3:
            return 'rd'
        default:
            return 'th'
    }
}

// Static Date functions
Date.isLeapYear = function (year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

Date.getDaysInMonth = function (year, month) {
    return [31, Date.isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month]
}

Date.isValidDateString = function (dateString) {
    const date = new Date(dateString)
    return !isNaN(date.getTime()) && dateString === date.toISOString()
}

if (!Date.prototype.format) {
    Date.prototype.format = function (format) {
        return moment(this).format(format)
    }
}

if (!Date.prototype.getTotalYearsSince) {
    Date.prototype.getTotalYearsSince = function () {
        var today = new Date()
        var birthDate = this
        var age = today.getFullYear() - birthDate.getFullYear()
        var m = today.getMonth() - birthDate.getMonth()
        var unit = 'years'
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--
            m--
        }
        if (age == 0) {
            age = m
            unit = 'months'
        }
        if (age == 0) {
            age = birthDate.daysUntil(today)
            unit = 'days'
        }
        return {
            age,
            unit
        }
    }
}

// Instance Date functions
if (!Date.prototype.daysFromNow) {
    Date.prototype.daysFromNow = function (referenceDate) {
        const today = referenceDate || new Date()
        return today.daysUntil(this)
    }
}

if (!Date.prototype.daysUntil) {
    Date.prototype.daysUntil = function (date, fractional) {
        if (!(date instanceof Date)) {
            throw 'Parameter for daysUntil is not a date'
        }

        if (fractional) {
            return (date.getTime() - this.getTime()) / millisecondsIn.day
        }
        return Math.floor((date.getMidnight() - this.getMidnight()) / millisecondsIn.day)
    }
}

if (!Date.prototype.isToday) {
    Date.prototype.isToday = function (referenceDate) {
        const today = referenceDate || new Date()
        return (
            this.getDate() == today.getDate() &&
            this.getMonth() == today.getMonth() &&
            this.getFullYear() == today.getFullYear()
        )
    }
}

if (!Date.prototype.addMilliseconds) {
    Date.prototype.addMilliseconds = function (millis) {
        return new Date(this.getTime() + millis)
    }
}

if (!Date.prototype.addSeconds) {
    Date.prototype.addSeconds = function (seconds) {
        return this.addMilliseconds(seconds * millisecondsIn.second)
    }
}

if (!Date.prototype.addMinutes) {
    Date.prototype.addMinutes = function (minutes) {
        return this.addMilliseconds(minutes * millisecondsIn.minute)
    }
}

if (!Date.prototype.addHours) {
    Date.prototype.addHours = function (hours) {
        return this.addMilliseconds(hours * millisecondsIn.hour)
    }
}

if (!Date.prototype.addDays) {
    Date.prototype.addDays = function (days) {
        return this.addMilliseconds(days * millisecondsIn.day)
    }
}

if (!Date.prototype.addWeeks) {
    Date.prototype.addWeeks = function (weeks) {
        return this.addMilliseconds(weeks * millisecondsIn.week)
    }
}

if (!Date.prototype.isLeapYear) {
    Date.prototype.isLeapYear = function () {
        return Date.isLeapYear(this.getFullYear())
    }
}
if (!Date.prototype.getDaysInMonth) {
    Date.prototype.getDaysInMonth = function () {
        return Date.getDaysInMonth(this.getFullYear(), this.getMonth())
    }
}

if (!Date.prototype.addMonths) {
    Date.prototype.addMonths = function (value) {
        var newDate = new Date(this)
        var n = newDate.getDate()
        newDate.setDate(1)
        newDate.setMonth(newDate.getMonth() + value)
        newDate.setDate(Math.min(n, newDate.getDaysInMonth()))
        return newDate
    }
}

if (!Date.prototype.addYears) {
    Date.prototype.addYears = function (years) {
        var newD = new Date(this)
        newD.setFullYear(this.getFullYear() + years)
        return newD
    }
}

if (!Date.prototype.getFirstDayOfMonth) {
    Date.prototype.getFirstDayOfMonth = function () {
        return new Date(this.getFullYear(), this.getMonth(), 1)
    }
}

if (!Date.prototype.getLastDayOfMonth) {
    Date.prototype.getLastDayOfMonth = function () {
        return new Date(this.getFullYear(), this.getMonth() + 1, 0)
    }
}

if (!Date.prototype.getAmPm) {
    Date.prototype.getAmPm = function () {
        if (this.getHours() >= 12) {
            return 'PM'
        }
        return 'AM'
    }
}

if (!Date.prototype.getEndOfDay) {
    Date.prototype.getEndOfDay = function () {
        var end = new Date(this)
        end.setHours(23)
        end.setMinutes(59)
        end.setSeconds(59)
        end.setMilliseconds(999)
        return end
    }
}

if (!Date.prototype.getStartOfDay) {
    Date.prototype.getStartOfDay = function () {
        return this.getMidnight()
    }
}

if (!Date.prototype.getMidnight) {
    Date.prototype.getMidnight = function () {
        var midnight = new Date(this)
        midnight.setHours(0)
        midnight.setMinutes(0)
        midnight.setSeconds(0)
        midnight.setMilliseconds(0)
        return midnight
    }
}

if (!Date.prototype.between) {
    Date.prototype.between = function (date1, date2) {
        if (typeof date1.getTime !== 'function' && typeof date2.getTime !== 'function') {
            throw 'Arguments must be date objects'
        }
        var thisTime = this.getTime()
        if (date1 && date2) {
            var d1Time = date1.getTime()
            var d2Time = date2.getTime()

            var max = Math.max(d1Time, d2Time)
            var min = Math.min(d1Time, d2Time)

            return thisTime <= max && thisTime >= min
        } else if (date1) {
            return thisTime >= date1.getTime()
        } else if (date2) {
            return thisTime <= date2.getTime()
        }
        return true
    }
}

if (!Date.prototype.toHyphenatedDateString) {
    Date.prototype.toHyphenatedDateString = function () {
        return this.toLocaleDateString().replaceAll('/', '-')
    }
}

if (!Date.prototype.toISODateString) {
    Date.prototype.toISODateString = function (separator) {
        separator = separator || '-'
        var year = this.getFullYear()
        var month = this.getMonth() + 1 // 0 indexed
        var day = this.getDate()
        return `${year}${separator}${month}${separator}${day}`
    }
}

if (!Date.prototype.isValid) {
    Date.prototype.isValid = function () {
        return !isNaN(this)
    }
}

if (!Date.prototype.humanize_date_diff) {
    Date.prototype.humanize_date_diff = function (referenceDate) {
        const today = referenceDate || new Date()

        if (this.isToday(today)) {
            return 'today'
        }

        var unitCount = this.daysFromNow(today)

        if (unitCount == 1) {
            return 'tomorrow'
        }
        if (unitCount == -1) {
            return 'yesterday'
        }

        return this.humanize_time_diff(today)
    }
}

if (!Date.prototype.humanize_time_diff) {
    Date.prototype.humanize_time_diff = function (referenceDate, showDifferentiator) {
        const destination = this
        if (typeof referenceDate == 'boolean') {
            showDifferentiator = referenceDate
            referenceDate = null
        }
        const origin = referenceDate || new Date()
        const diffInMilliseconds = Math.abs(destination - origin)

        if (diffInMilliseconds < millisecondsIn.second) {
            return 'now'
        }

        const isInFuture = destination - origin > 0
        const differentiator = isInFuture ? 'away' : 'ago'

        const timespansReversed = Object.keys(millisecondsIn)
            .map((key) => {
                return {
                    unit: key,
                    milliseconds: millisecondsIn[key]
                }
            })
            .defaultSort((obj) => obj.milliseconds)
            .reverse()

        let unit = 'second'
        let unitCount = 1

        for (let timespan of timespansReversed) {
            const timespanInMilliseconds = timespan.milliseconds
            const timespanDiff = diffInMilliseconds / timespanInMilliseconds

            if (timespanDiff >= 1) {
                unit = timespan.unit
                unitCount = Math.floor(timespanDiff)
                break
            }
        }

        const pluralSuffix = unitCount !== 1 ? 's' : ''
        var result = `${unitCount} ${unit}${pluralSuffix}`

        if (showDifferentiator) {
            result += ` ${differentiator}`
        }
        return result
    }
}

if (!Date.prototype.getMonthName) {
    Date.prototype.getMonthName = function () {
        return monthNames[this.getMonth()]
    }
}

if (!Date.prototype.getDayOfMonth) {
    Date.prototype.getDayOfMonth = function () {
        var day = this.getDate()
        return day + nth(day)
    }
}

if (!Date.prototype.equals) {
    Date.prototype.equals = function (rightVal) {
        return this.getTime() === (rightVal && rightVal.getTime())
    }
}

if (!Date.prototype.toLocalISOString) {
    Date.prototype.toLocalISOString = function () {
        var z = (n) => ('0' + n).slice(-2)
        var zz = (n) => ('00' + n).slice(-3)
        var off = this.getTimezoneOffset()
        var sign = off > 0 ? '-' : '+'
        off = Math.abs(off)

        return (
            this.getFullYear() +
            '-' +
            z(this.getMonth() + 1) +
            '-' +
            z(this.getDate()) +
            'T' +
            z(this.getHours()) +
            ':' +
            z(this.getMinutes()) +
            ':' +
            z(this.getSeconds()) +
            '.' +
            zz(this.getMilliseconds()) +
            sign +
            z((off / 60) | 0) +
            ':' +
            z(off % 60)
        )
    }
}

if (!Date.prototype.to12HrTimeFormat) {
    Date.prototype.to12HrTimeFormat = function () {
        return this.toLocaleString('en-US', {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true
        })
    }
}

if (!Date.prototype.toLocalTime) {
    Date.prototype.toLocalTime = function () {
        var hour = this.getHours()
        var mins = this.getMinutes().zeroPad(2)
        if (hour > 12) {
            hour = hour - 12
        }
        return `${hour}:${mins} ${this.getAmPm()}`
    }
}

if (!Date.prototype.toLocalTime24Hr) {
    Date.prototype.toLocalTime24Hr = function () {
        var hour = this.getHours().zeroPad(2)
        var mins = this.getMinutes().zeroPad(2)
        return `${hour}:${mins}`
    }
}

if (!Date.prototype.toAmericanDateString) {
    Date.prototype.toAmericanDateString = function () {
        var year = this.getFullYear()
        var month = this.getMonth() + 1 // 0 indexed
        var day = this.getDate()
        return `${month.padStart(2, 0)}/${day.padStart(2, 0)}/${year}`
    }
}
